import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  AppEventFull,
  AuthorityModel,
  CalculateResponse,
  CityRemainderProductModel,
  DiscountsResponse,
  OrderAllResponse,
  OrderEvent,
  OrderExpiration,
  OrderResponse,
  PagedList,
  PickupTimeCount,
  PromoCodeModel,
  ReceiptModel,
  RemainderProductModel,
  TrackingModel
} from '../interface';
import { firstValueFrom, Observable, ReplaySubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AddCommentRequest,
  AtolFiscalResult, CashboxResourceService,
  CommentModel,
  CompanyModel,
  ConfectioneryResourceService,
  DecorFilter,
  DeliveryType,
  DiscountResultModel,
  Entity, OralGatewayResponse,
  OrderAction,
  OrderResourceService,
  PaymentType,
  ProductDateCountInfoModel,
  ProductResourceService,
  ProductShortModel,
  SeekDirection,
  SortType,
  SourceSystem,
  SquareupReceiptModel,
  TerminalActionResponse,
  UiLabel,
  WebkassaFiscalResult
} from '../vn-api';

@Injectable({
  providedIn: 'root'
})
export class HxOrderService {
  private printListSubject = new Subject<ReceiptModel[]>();
  private updateOrderSubject = new Subject<void>();
  private receiveAfterTimeChangeSubject = new ReplaySubject<OrderEvent<ReceiveAfterTime>>(1);
  private recalcDiscountsSubject = new Subject<void>();

  readonly printListCheckObs = this.printListSubject.asObservable();
  readonly updateOrderObs = this.updateOrderSubject.asObservable();
  readonly receiveAfterTimeChangeObs = this.receiveAfterTimeChangeSubject.asObservable();
  readonly recalcDiscountsObs = this.recalcDiscountsSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private confectioneryResourceService: ConfectioneryResourceService,
    private orderResource: OrderResourceService,
    private productResource: ProductResourceService,
    private cashboxResource: CashboxResourceService
  ) {
  }

  notifyReceiveAfterTimeChanged(orderId: number, event: ReceiveAfterTime) {
    this.receiveAfterTimeChangeSubject.next({id: orderId, event: event});
  }

  printCheck(check: ReceiptModel): void {
    this.printListSubject.next([check]);
  }

  printCheckList(list: ReceiptModel[]): void {
    this.printListSubject.next(list);
  }

  orderUpdated() {
    this.updateOrderSubject.next();
  }

  recalcDiscounts() {
    this.recalcDiscountsSubject.next();
  }

  getReservedOrders(date: string, storeId: number): Observable<PickupTimeCount[]> {
    return this.orderResource.reservedOrderCount(date, storeId) as unknown as Observable<PickupTimeCount[]>;
  }

  sendWhatsappTemplate(req: { phone: string; templateName: string; params?: Record<string, string> }): Promise<void> {
    return firstValueFrom(this.httpClient.post<void>(`/api/vn/orders/whatsapp-template`, req));
  }

  getRemainders(params: { storeIds?: number[], date: string, categoryIds: number[] }): Promise<RemainderProductModel[]> {
    return firstValueFrom(this.orderResource.remainderProducts(params.date, undefined, params.categoryIds, params.storeIds)) as unknown as Promise<RemainderProductModel[]>;
  }

  getRemaindersByCityId(cityId: number, params: { date: string; categoryIds: number[]; brandId: number; }): Promise<CityRemainderProductModel[]> {
    return firstValueFrom(this.orderResource.cityRemainderProducts(cityId, params.date, params.brandId, undefined, params.categoryIds)) as unknown as Promise<CityRemainderProductModel[]>;
  }

  generateInvoice(orderId: number, type: 'product' | 'service' | 'payment'): Observable<any> {
    return this.httpClient.get(`/api/vn/orders/${orderId}/invoice?type=${type}`,
      {responseType: 'arraybuffer', observe: 'response'});
  }

  getAppEventById(id: number): Observable<AppEventFull> {
    return this.httpClient.get<AppEventFull>(`/api/vn/events/${id}`);
  }

  getOrderReceipt(params: { orderId: number, eventId?: number }): Promise<ReceiptModel> {
    return firstValueFrom(this.orderResource.getReceipt(params.orderId)) as unknown as Promise<ReceiptModel>;
  }

  saveFiscalizationData(orderId: number, fiscalDetails: AtolFiscalResult | WebkassaFiscalResult): Observable<void> {
    return this.orderResource.saveFiscalDetails(orderId, fiscalDetails as any);
  }

  getOrderReceipts(body: { orderIds: number[] }): Observable<ReceiptModel[]> {
    return this.orderResource.getReceipts(body as any) as unknown as Observable<ReceiptModel[]>;
  }

  getOrderById(id: number): Promise<OrderResponse> {
    return firstValueFrom(this.orderResource.getOrderById(id)) as unknown as Promise<OrderResponse>;
  }

  getFullOrderById(id: number): Promise<OrderResponse> {
    return firstValueFrom(this.orderResource.getOrderFullById(id)) as unknown as Promise<OrderResponse>;
  }

  getTracking(id: number): Promise<TrackingModel> {
    return firstValueFrom(this.orderResource.getTracking(id)) as unknown as Promise<TrackingModel>;
  }

  getCompanyInfo(id: number): Observable<{ company: CompanyModel, authority: AuthorityModel }> {
    return this.orderResource.getCompanyInfo(id) as unknown as Observable<{ company: CompanyModel, authority: AuthorityModel }>;
  }

  saveCompanyInfo(orderId: number, req: CompanyModel, params: { clientId?: number }): Observable<void> {
    return this.orderResource.saveCompany(orderId, params.clientId as number, req);
  }

  saveAuthorityInfo(orderId: number, req: AuthorityModel): Observable<void> {
    return this.orderResource.saveAuthority(orderId, req);
  }

  getOrderList(params?: {
    limit?: number,
    seek?: string,
    seekDir?: SeekDirection,
    sort?: SortType,
    orderBy?: 'id' | 'number' | 'date',
    query?: string,
    clientId?: number,
    cityId?: number,
    storeIds?: number[],
    actions?: OrderAction[],
    deliveryType?: DeliveryType,
    clientType?: Entity,
    date?: string,
    fromDate?: string,
    toDate?: string,
    decor?: DecorFilter,
    courierId?: number,
    decored?: boolean,
    archived?: boolean,
    excludeChildren?: boolean,
    includeWaitPayment?: boolean,
    activeCart?: boolean,
    fiscalNumber?: string,
    sourceSystem?: string,
    important?: boolean,
    operatorId?: number,
    productInfoIds?: number[],
    refundFromDate?: string,
    refundToDate?: string,
  }) {
    return firstValueFrom(this.orderResource.getOrderList(
      params?.actions,
      params?.activeCart,
      params?.archived,
      params?.cityId,
      params?.clientId,
      params?.clientType,
      params?.courierId,
      params?.date,
      params?.decor,
      params?.decored,
      params?.deliveryType,
      params?.excludeChildren,
      params?.fiscalNumber,
      params?.fromDate,
      params?.important,
      params?.includeWaitPayment,
      params?.limit,
      params?.operatorId,
      params?.orderBy,
      params?.productInfoIds,
      params?.query,
      params?.refundFromDate,
      params?.refundToDate,
      params?.seek,
      params?.seekDir,
      params?.sort,
      params?.sourceSystem,
      params?.storeIds,
      params?.toDate,
    ));
  }

  getOrderListByProductInfoId(id: number, params?: {
    storeIds?: number[];
    limit?: number;
    page?: number;
    sort?: SortType;
    orderBy?: string;
    date?: string;
    actions?: OrderAction[]
  }): Observable<PagedList<OrderAllResponse>> {
    return this.orderResource.getByProductInfo(id, params?.date, params?.limit, params?.page, params?.storeIds) as unknown as Observable<PagedList<OrderAllResponse>>;
  }

  createOrder(request: CreateCartRequest): Promise<OrderResponse> {
    return firstValueFrom(this.orderResource.createOrder(request as any)) as unknown as Promise<OrderResponse>;
  }

  updateOrder(id: number, body: UpdateOrderRequest): Promise<UpdateOrderResponse> {
    return firstValueFrom(this.orderResource.updateOrder(id, body as any)) as unknown as Promise<UpdateOrderResponse>;
  }

  transferOrder(id: number, body: TransferOrderRequest): Promise<TransferOrderResponse> {
    return firstValueFrom(this.orderResource.transferOrder(id, body as any)) as unknown as Promise<TransferOrderResponse>;
  }

  cancelOrder(id: number, body: any): Observable<void> {
    return this.orderResource.cancelOrder(id, body);
  }

  getOrderCartById(id: number): Promise<OrderResponse> {
    return firstValueFrom(this.orderResource.getCart(id)) as unknown as Promise<OrderResponse>;
  }

  getActiveOrderCart(): Observable<OrderResponse> {
    return this.orderResource.getActiveCart() as unknown as Observable<OrderResponse>;
  }

  getCartExpiration(orderId: number): Observable<OrderExpiration> {
    return this.orderResource.getCartExpiration(orderId) as unknown as Observable<OrderExpiration>;
  }

  prolongCartExpiration(orderId: number): Observable<OrderExpiration> {
    return this.orderResource.prolongCartExpiration(orderId) as unknown as Observable<OrderExpiration>;
  }

  cancelCart(orderId: number): Promise<void> {
    return firstValueFrom(this.orderResource.cancelCart(orderId));
  }

  getCartProducts(orderId: number): Observable<ProductShortModel[]> {
    return this.orderResource.getCartProducts(orderId);
  }

  addCartProduct(orderId: number, params: { priceId: number }): Promise<ProductShortModel> {
    return firstValueFrom(this.orderResource.addCartProduct(orderId, params.priceId));
  }

  removeCartProduct(orderId: number, productId: number): Observable<void> {
    return this.orderResource.removeCartProduct(orderId, productId);
  }

  setImportant(orderId: number, important: boolean): Observable<void> {
    return this.orderResource.markImportant(orderId, {value: important} as any);
  }

  downCartProduct(orderId: number, productId: number): Observable<void> {
    return this.orderResource.subtractCartProduct(orderId, productId);
  }

  checkPaymentStatus(params: { orderId: number, type: PaymentType }): Observable<{ paid: boolean }> {
    return this.orderResource.checkPaymentStatus(params.orderId) as unknown as Observable<{ paid: boolean }>;
  }

  isOrderArchived(orderId: number): Observable<boolean> {
    return this.orderResource.isArchived(orderId).pipe(map((response: any) => response.result));
  }

  unarchive(orderId: number): Observable<any> {
    return this.orderResource.unarchive(orderId);
  }

  startOrderModification(orderId: number): Observable<void> {
    return this.orderResource.startModify(orderId);
  }

  getChildOrders(orderId: number): Promise<OrderResponse[]> {
    return firstValueFrom(this.orderResource.getChildren(orderId)) as unknown as Promise<OrderResponse[]>;
  }

  getReceiveAfterTime(orderId: number, storeId?: number): Promise<ReceiveAfterTime> {
    return firstValueFrom(this.orderResource.getReceiveAfterTime(orderId, storeId)) as unknown as Promise<ReceiveAfterTime>;
  }

  reservePickupTime(param: { pickupTime: string, orderId: number }): Promise<void> {
    return firstValueFrom(this.orderResource.updatePickupTime(param.orderId, {time: param.pickupTime} as any));
  }

  checkPaymentStatusByEventId(params: { orderId: number, eventId: number }): Observable<{ paid: boolean }> {
    return this.orderResource.refreshPaymentStatus(params.eventId, params.orderId) as unknown as Observable<{ paid: boolean }>;
  }

  checkDiscount(body: CalculateDiscountRequest): Observable<CalculateResponse> {
    return this.orderResource.calcDiscounts(body as any) as unknown as Observable<CalculateResponse>;
  }

  getDiscountList(orderId: number): Promise<DiscountsResponse> {
    return firstValueFrom(this.orderResource.getDiscounts(orderId)) as unknown as Promise<DiscountsResponse>;
  }

  sendOtpCode(params: { orderId: number, clientId: number }): Observable<void> {
    return this.orderResource.sendOtpCode(params.orderId, params.clientId);
  }

  confirmOtpCode(params: { orderId: number, clientId: number, code: string }): Observable<any> {
    return this.orderResource.confirmOtpCode(params.orderId, params.clientId, params.code);
  }

  getPromoCode(orderId: number): Observable<PromoCodeModel | undefined> {
    return this.orderResource.getPromoCode(orderId)
      .pipe(map(r => r === null ? undefined : r)) as unknown as Observable<PromoCodeModel | undefined>;
  }

  applyPromoCode(orderId: number, code: string): Observable<{ promoCode: PromoCodeModel, priority: number }> {
    return this.orderResource.applyPromoCode(orderId, {code} as any) as unknown as Observable<{ promoCode: PromoCodeModel, priority: number }>;
  }

  confirmPromoCode(id: number, confirmCode: string): Observable<PromoCodeModel> {
    return this.orderResource.confirmPromoCode(id, {confirmCode} as any) as unknown as Observable<PromoCodeModel>;
  }

  cancelPromoCode(orderId: number): Observable<void> {
    return this.orderResource.cancelPromoCode(orderId);
  }

  sendPayInvoice(orderId: number, body: {
    paymentType: PaymentType;
    phone: string;
  }): Promise<{ total: number }> {
    return firstValueFrom(this.orderResource.sendPayPushNotification(orderId, {
      paymentType: body.paymentType,
      phone: body.phone,
    }));
  }

  getProductCountsByCategoryAndDate(params: { fromDate?: string, toDate?: string, storeIds?: number[] }): Promise<ProductDateCountInfoModel[]> {
    return firstValueFrom(this.productResource.getProductCountsByCategoryAndDate(params.fromDate, params.storeIds, params.toDate));
  }

  updateOrderDeliveryLatAndLong(orderId: number, body: { deliveryLat: number, deliveryLong: number }): Observable<void> {
    return this.orderResource.updateLatLong(orderId, body as any);
  }

  decorOrder(orderId: number): Promise<void> {
    return firstValueFrom(this.confectioneryResourceService.decorOrder(orderId));
  }

  updateApplyCoins(orderId: number, value: boolean) {
    return firstValueFrom(this.orderResource.updateApplyCoins(orderId, value));
  }

  getCommentsByOrderId(orderId: number): Promise<CommentModel[]> {
    return firstValueFrom(this.orderResource.getCommentsByOrderId(orderId));
  }

  addComment(orderId: number, request: AddCommentRequest): Promise<CommentModel> {
    return firstValueFrom(this.orderResource.saveComment(orderId, request));
  }

  getSquareupReceiptUrl(orderId: number): Promise<SquareupReceiptModel> {
    return firstValueFrom(this.orderResource.getSquareupReceiptUrl(orderId));
  }

  printSquareupReceipt(orderId: number): Promise<TerminalActionResponse> {
    return firstValueFrom(this.orderResource.printSquareupReceipt(orderId));
  }

  searchByBarcode(barcode: string, storeId: number): Promise<OralGatewayResponse> {
    return firstValueFrom(this.cashboxResource.searchByBarcode(barcode, storeId));
  }
}

export interface ReceiveAfterTime {
  productTitle: UiLabel;
  time: string;
}

export interface TransferOrderRequest {
  orderDate: string;
  orderTime: string;
  clientId: number;
  clientPhone: string;
  storeId: number;
  deliveryType: DeliveryType;
  address?: string;
  deliveryLongitude?: number;
  deliveryLatitude?: number;
  rangeId?: number;
  total: number;
  discounts: DiscountResultModel[];
  entrance?: string;
  flat?: string;
  floor?: string;
}

export interface TransferOrderResponse {
  uniqueNumber: string;
  orderId: number;
}

export interface UpdateOrderRequest {
  orderDate: string;
  orderTime: string;
  clientId: number;
  clientPhone: string;
  clientType: Entity;
  storeId: number;
  deliveryType: DeliveryType;
  discounts: DiscountResultModel[];
  total: number;
  subTotal: number;
  products: Product[];
  recipientClientId?: number;
  recipientClientPhone?: string;
  from?: SourceSystem;
  note?: string;
  decorText?: string;
  debt?: boolean;
  cancelReason?: string;
  important?: boolean;
  address?: string;
  admDiv?: string;
  entrance?: string;
  floor?: string;
  flat?: string;
  deliveryLongitude?: number;
  deliveryLatitude?: number;
  addressNote?: string;
  files?: number[];
  cartRangeId?: number;
  rejectionId?: number;
  referrer?: string;
  withoutReceipt?: boolean;
  comment?: string;
}

export interface UpdateOrderResponse {
  number: number;
  uniqueNumber: string;
}

export interface CreateCartRequest {
  callId?: string;
  date: string;
  storeId: number;
  phone?: string;
  rangeId?: number;
  address?: {
    address: string;
    latitude?: number;
    longitude?: number;
    entrance?: string;
    floor?: string;
    flat?: string;
    addressNote?: string;
  };
  products: Product[];
}

export interface CartChangeEvent {
  isDebt: boolean;
  products: ProductShortModel[];
  discounts: DiscountResultModel[];
  total: number;
  subTotal: number;
}

export interface CalculateDiscountRequest {
  results: DiscountResultModel[];
  order: {
    id?: number;
    storeId: number;
    date: string;
    time: string;
    employeeId?: number;
    clientId?: number;
    clientType: Entity;
    deliveryType: DeliveryType;
  };
  products: CalculateDiscountProduct[];
  sourceSystem?: SourceSystem;
}

interface Product {
  productDefinitionId: number;
  amount: number;
  priceAmount: number;
}

interface CalculateDiscountProduct {
  priceId: number;
  amount: number;
}
